import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import identity from 'ramda/src/identity';
import memoizeWith from 'ramda/src/memoizeWith';
import isEmpty from 'ramda/src/isEmpty';
import LayoutContainer from 'shared/layout-container';
import Routes from 'apps/portal/routes.erb';
import layoutHOC from 'helpers/layout-hoc';
import Button from '@fountain/ui-kit/components/button';
import Form from '@fountain/ui-kit/components/form';
import OpeningsDirectoryLayout from 'apps/portal/layouts/openings-directory';

class OpeningsDirectoryPicker extends PureComponent {
  static propTypes = {
    accountSlug: PropTypes.string.isRequired,
    ax: PropTypes.bool,
    embeddable: PropTypes.bool,
    brands: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    })),
    brandsCountries: PropTypes.arrayOf(PropTypes.shape({
      brandId: PropTypes.string,
      countryCode: PropTypes.string,
    })),
    change: PropTypes.func.isRequired,
    countries: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      code: PropTypes.string,
    })),
    form: PropTypes.shape({
      brandSlug: PropTypes.string,
      countryCode: PropTypes.string,
    }),
    urlParams: PropTypes.string,
  }

  static defaultProps = {
    ax: false,
    brands: [],
    brandsCountries: [],
    countries: [],
    form: {},
    embeddable: false,
    urlParams: '',
  }

  _getLink = () => {
    const {
      accountSlug, form, embeddable, urlParams,
    } = this.props;

    const baseUrl = embeddable ?
      Routes.embed_directory_openings_path(accountSlug, {
        brand_slug: form.brandSlug,
        country_code: form.countryCode,
      }) :
      Routes.directory_openings_path(accountSlug, {
        brand_slug: form.brandSlug,
        country_code: form.countryCode,
      });

    return `${baseUrl}${urlParams ? '?' : ''}${urlParams}`;
  }

  _handleCountryCodeChange = (e, curr, prev) => {
    if (curr !== prev) {
      this.props.change('brandSlug', null);
    }
  }

  _getBrandsByCountry = memoizeWith(identity, (code) => {
    if (!code) {
      return [];
    }

    const { brandsCountries, brands } = this.props;
    const brandIds = brandsCountries.reduce((acc, bc) => {
      if (bc.countryCode === code) {
        acc.push(bc.brandId);
      }

      return acc;
    }, []);

    return brands.filter(b => brandIds.includes(b.id));
  })

  render() {
    const {
      brands,
      countries,
      form,
      ax,
    } = this.props;
    const countryInputIdentifier = 'countryCode';
    const companyInputIdentifier = 'brandSlug';
    const isButtonDisabled = !form.brandSlug || !form.countryCode;
    const countryLabel = ax ? <Form.Label htmlFor={countryInputIdentifier}>Country</Form.Label> : null;
    const companyLabel = ax ? <Form.Label htmlFor={companyInputIdentifier}>Company</Form.Label> : null;
    const button = ax ? (
      <div className="ax-button-container">
        <a
          href={this._getLink()}
          className={`ax-button${(isButtonDisabled ? ' disabled' : '')}`}
          id="search-openings"
          onClick={(event) => {
            if (isButtonDisabled) {
              event.preventDefault();
            }
          }}
        >
          <span>Search Openings</span>
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
            <path d="M6.434 12.27l3.445-3.445-3.445-3.445 1.055-1.055 4.5 4.5-4.5 4.5z" />
          </svg>
        </a>
      </div>
    ) : (
      <Button
        disabled={isButtonDisabled}
        fullWidth
        size="small"
        href={!isButtonDisabled ? this._getLink() : null}
      >
        Search Openings
      </Button>
    );

    return (
      <LayoutContainer centered size="xsmall">
        <Form>
          {countryLabel}
          <Form.Box>
            <Form.Select
              disabled={isEmpty(countries)}
              fullWidth
              id={countryInputIdentifier}
              name={countryInputIdentifier}
              onChange={this._handleCountryCodeChange}
              placeholder="Select Country"
            >
              {/*
                Each option's testid carries its VALUE, not its label:
                @fountain/ui-kit portals its <Select> menu to <body> with
                class-only hooks and renders it only while open, so an e2e can
                otherwise address an option by display text alone — and that text
                is a CLDR translation here (and, for the brands below, is also the
                layout's nav heading). See tools/hire-e2e JobsDirectoryPage.
              */}
              {countries.map(country => (
                <Form.Select.Option
                  key={country.code}
                  value={country.code}
                  data-testid={`picker-country-option-${country.code}`}
                >
                  {country.name}
                </Form.Select.Option>
              ))}
            </Form.Select>
          </Form.Box>
          {companyLabel}
          <Form.Box>
            <Form.Select
              disabled={isEmpty(brands) || !form.countryCode}
              fullWidth
              id={companyInputIdentifier}
              name={companyInputIdentifier}
              placeholder="Select Company"
            >
              {this._getBrandsByCountry(form.countryCode).map(brand => (
                <Form.Select.Option
                  key={brand.id}
                  value={brand.slug}
                  data-testid={`picker-brand-option-${brand.slug}`}
                >
                  {brand.name}
                </Form.Select.Option>
              ))}
            </Form.Select>
          </Form.Box>
          {button}
        </Form>
      </LayoutContainer>
    );
  }
}

export default layoutHOC(OpeningsDirectoryPicker, OpeningsDirectoryLayout);

